import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Mail,
  Clock,
  Trophy,
  Medal,
  Award,
  Users,
  Loader2,
  CalendarDays
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  Gold: { bg: "bg-yellow-100", text: "text-yellow-800" },
  Silver: { bg: "bg-slate-100", text: "text-slate-700" },
  Bronze: { bg: "bg-orange-100", text: "text-orange-800" },
  Unranked: { bg: "bg-slate-50", text: "text-slate-500" }
};

const TierIcon = ({ tier, className = "h-3 w-3" }) => {
  switch (tier) {
    case "Gold": return <Trophy className={`${className} text-yellow-500`} />;
    case "Silver": return <Medal className={`${className} text-slate-400`} />;
    case "Bronze": return <Award className={`${className} text-orange-500`} />;
    default: return <Users className={`${className} text-slate-400`} />;
  }
};

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateLeads, setSelectedDateLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthStats, setMonthStats] = useState({ emails: 0, followups: 0 });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get first day of month and total days
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Generate calendar days
  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/calendar/month`, {
        params: { year, month: month + 1 }
      });
      setCalendarData(response.data.days || {});
      setMonthStats({
        emails: response.data.total_emails || 0,
        followups: response.data.total_followups || 0
      });
    } catch (error) {
      console.error("Calendar fetch error:", error);
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDateDetails = async (dateStr) => {
    try {
      const response = await axios.get(`${API}/calendar/date/${dateStr}`);
      setSelectedDateLeads(response.data);
    } catch (error) {
      console.error("Date details error:", error);
      setSelectedDateLeads([]);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, [year, month]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDate(null);
    setSelectedDateLeads([]);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDate(null);
    setSelectedDateLeads([]);
  };

  const handleDateClick = (day) => {
    if (!day) return;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    fetchDateDetails(dateStr);
  };

  const formatDate = (dateStr) => {
    // Parse date as local time to avoid timezone offset issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const isWeekend = (dayIndex) => dayIndex % 7 === 0 || dayIndex % 7 === 6;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1C2A25] font-['Outfit'] tracking-tight">Outreach Calendar</h1>
          <p className="text-slate-500 mt-1">
            Track emails sent and follow-ups by date
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Card className="px-4 py-2">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#2E7AD1]">{monthStats.emails}</p>
                <p className="text-xs text-slate-500">Emails Sent</p>
              </div>
              <div className="h-8 w-px bg-slate-200"></div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-500">{monthStats.followups}</p>
                <p className="text-xs text-slate-500">Follow-ups Due</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={handlePrevMonth}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <CardTitle className="text-xl">
                {months[month]} {year}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={handleNextMonth}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#2E7AD1]" />
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {/* Day headers */}
                {daysOfWeek.map((day, i) => (
                  <div
                    key={day}
                    className={cn(
                      "text-center text-xs font-medium py-2",
                      i === 0 || i === 6 ? "text-slate-400" : "text-slate-600"
                    )}
                  >
                    {day}
                  </div>
                ))}

                {/* Calendar days */}
                {calendarDays.map((day, index) => {
                  const dateStr = day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
                  const dayData = dateStr ? calendarData[dateStr] : null;
                  const hasEmails = dayData?.emails > 0;
                  const hasFollowups = dayData?.followups > 0;
                  const isSelected = dateStr === selectedDate;

                  return (
                    <div
                      key={index}
                      onClick={() => handleDateClick(day)}
                      className={cn(
                        "aspect-square p-1 rounded-lg cursor-pointer transition-all",
                        !day && "cursor-default",
                        day && "hover:bg-slate-100",
                        isToday(day) && "ring-2 ring-[#2E7AD1] ring-offset-1",
                        isSelected && "bg-[#2E7AD1]/10",
                        isWeekend(index) && day && "bg-slate-50"
                      )}
                    >
                      {day && (
                        <div className="h-full flex flex-col">
                          <span className={cn(
                            "text-sm font-medium",
                            isToday(day) ? "text-[#2E7AD1]" : "text-slate-700"
                          )}>
                            {day}
                          </span>
                          <div className="flex-1 flex flex-col justify-end gap-0.5">
                            {hasEmails && (
                              <div className="flex items-center gap-1 text-xs">
                                <Mail className="h-3 w-3 text-[#2E7AD1]" />
                                <span className="text-[#2E7AD1] font-medium">{dayData.emails}</span>
                              </div>
                            )}
                            {hasFollowups && (
                              <div className="flex items-center gap-1 text-xs">
                                <Clock className="h-3 w-3 text-orange-500" />
                                <span className="text-orange-500 font-medium">{dayData.followups}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Legend */}
            <div className="mt-4 pt-4 border-t flex items-center justify-center gap-6 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <Mail className="h-3 w-3 text-[#2E7AD1]" />
                <span>Emails Sent</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-orange-500" />
                <span>Follow-ups Due</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded ring-2 ring-[#2E7AD1]"></div>
                <span>Today</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-slate-400" />
              {selectedDate ? formatDate(selectedDate) : "Select a Date"}
            </CardTitle>
            <CardDescription>
              {selectedDate 
                ? `${selectedDateLeads.length} activities`
                : "Click on a date to see details"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedDate ? (
              <div className="text-center py-12 text-slate-400">
                <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Select a date on the calendar</p>
                <p className="text-xs mt-1">to view emails and follow-ups</p>
              </div>
            ) : selectedDateLeads.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <p>No activities on this date</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {selectedDateLeads.map((lead, idx) => (
                    <Link 
                      key={lead.id || idx}
                      to={`/leads/${lead.id}`}
                      className="block"
                    >
                      <div className={cn(
                        "p-3 rounded-lg border transition-colors hover:border-slate-300",
                        lead.type === "email" ? "bg-blue-50/50 border-blue-100" : "bg-orange-50/50 border-orange-100"
                      )}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {lead.type === "email" ? (
                                <Mail className="h-4 w-4 text-[#2E7AD1]" />
                              ) : (
                                <Clock className="h-4 w-4 text-orange-500" />
                              )}
                              <span className={cn(
                                "text-xs font-medium",
                                lead.type === "email" ? "text-[#2E7AD1]" : "text-orange-600"
                              )}>
                                {lead.type === "email" ? "Email Sent" : `Follow-up #${lead.followup_number}`}
                              </span>
                            </div>
                            <p className="font-medium text-slate-900 mt-1 truncate">
                              {lead.company || "Unknown"}
                            </p>
                            <p className="text-xs text-slate-500 truncate">
                              {lead.first_name || lead.contact || "N/A"} {lead.last_name || ""}
                            </p>
                          </div>
                          <Badge className={cn(
                            "flex-shrink-0 text-xs",
                            TIER_STYLES[lead.company_tier]?.bg,
                            TIER_STYLES[lead.company_tier]?.text
                          )}>
                            <TierIcon tier={lead.company_tier} className="h-3 w-3 mr-1" />
                            {lead.company_tier}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
